import type { JSX } from 'react';
import type { ShellHydration } from '../../../api.js';
import type { Palette } from '../../../_theme/palette.js';
import type { SurveyQuestion } from '../types.js';
import { AddressField } from './fields/AddressField.js';
import { DateField } from './fields/DateField.js';
import { ConsentField } from './fields/ConsentField.js';
import { DateRangeField } from './fields/DateRangeField.js';
import { DropdownField } from './fields/DropdownField.js';
import { EmojiScaleField } from './fields/EmojiScaleField.js';
import { FileUploadField } from './fields/FileUploadField.js';
import { IframeField } from './fields/IframeField.js';
import { ImageChoiceField } from './fields/ImageChoiceField.js';
import { LongTextField } from './fields/LongTextField.js';
import { MarkdownField } from './fields/MarkdownField.js';
import { MatrixField } from './fields/MatrixField.js';
import { MaxDiffField } from './fields/MaxDiffField.js';
import { MultipleChoiceField } from './fields/MultipleChoiceField.js';
import { MultipleTextboxesField } from './fields/MultipleTextboxesField.js';
import { PhoneField } from './fields/PhoneField.js';
import { RankingField } from './fields/RankingField.js';
import { ScaleField } from './fields/ScaleField.js';
import { SectionField } from './fields/SectionField.js';
import { ShortTextField } from './fields/ShortTextField.js';
import { SignatureField } from './fields/SignatureField.js';
import { SingleChoiceField } from './fields/SingleChoiceField.js';
import { SliderField } from './fields/SliderField.js';
import { StarRatingField } from './fields/StarRatingField.js';
import { YesNoField } from './fields/YesNoField.js';

interface QuestionFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: unknown) => void;
  hydration: ShellHydration;
}

export function QuestionField({ palette, question, value, onChange, hydration }: QuestionFieldProps): JSX.Element {
  switch (question.type) {
    case 'shortText':
      return <ShortTextField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'email':
      return <ShortTextField palette={palette} question={question} value={value} onChange={onChange} inputType="email" />;
    case 'website':
    case 'url':
      return <ShortTextField palette={palette} question={question} value={value} onChange={onChange} inputType="url" />;
    case 'number':
      return <ShortTextField palette={palette} question={question} value={value} onChange={onChange} inputType="number" />;
    case 'date':
      return <DateField palette={palette} type="date" value={value} onChange={onChange} ariaLabel={question.label} />;
    case 'datetime':
      return <DateField palette={palette} type="datetime-local" value={value} onChange={onChange} ariaLabel={question.label} />;
    case 'timePicker':
      return <DateField palette={palette} type="time" value={value} onChange={onChange} ariaLabel={question.label} />;
    case 'monthYear':
      return <DateField palette={palette} type="month" value={value} onChange={onChange} ariaLabel={question.label} />;
    case 'phone':
      return <PhoneField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'longText':
    case 'commentBox':
      return <LongTextField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'rating':
      return <StarRatingField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'nps':
      return <ScaleField palette={palette} question={question} value={value} onChange={onChange} min={0} max={10} />;
    case 'csat':
    case 'ces':
    case 'likert':
    case 'scale':
      return (
        <ScaleField
          palette={palette}
          question={question}
          value={value}
          onChange={onChange}
          min={question.settings?.min ?? 1}
          max={question.settings?.max ?? 5}
        />
      );
    case 'slider':
      return <SliderField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'singleChoice':
    case 'choice':
      return <SingleChoiceField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'multipleChoice':
    case 'multiChoice':
    case 'checkboxes':
      return <MultipleChoiceField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'yesNo':
      return <YesNoField palette={palette} value={value} onChange={onChange} />;
    case 'dropdown':
      return <DropdownField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'emojiScale':
      return (
        <EmojiScaleField
          palette={palette}
          value={value}
          onChange={onChange}
          emojis={question.settings?.emojiIcons}
        />
      );
    case 'ranking':
      return <RankingField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'imageChoice':
      return <ImageChoiceField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'consent':
      return <ConsentField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'markdown':
      return <MarkdownField palette={palette} question={question} />;
    case 'section':
      return <SectionField palette={palette} question={question} />;
    case 'maxdiff':
      return <MaxDiffField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'iframe':
      return <IframeField question={question} />;
    case 'address':
      return <AddressField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'dateRange':
      return <DateRangeField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'multipleTextboxes':
      return <MultipleTextboxesField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'signature':
      return <SignatureField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'matrix':
    case 'matrixRating':
      return <MatrixField palette={palette} question={question} value={value} onChange={onChange} />;
    case 'fileUpload':
      return (
        <FileUploadField
          palette={palette}
          question={question}
          value={value}
          onChange={onChange}
          hydration={hydration}
        />
      );
    default:
      return <ShortTextField palette={palette} question={question} value={value} onChange={onChange} />;
  }
}
